import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  Users,
  Plus,
  ArrowLeft,
  Mail,
  Phone,
  Briefcase,
  Loader2,
  Trash2,
  Edit2,
  UserCheck,
  UserX,
  Building2,
  ChevronDown,
  Key,
  Shield,
  Eye,
  EyeOff,
  Copy,
  Search,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  RefreshCw,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AgentStaff() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [staff, setStaff] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [companyId, setCompanyId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('staff');
  
  // Dialog states
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  // Form states
  const [newStaff, setNewStaff] = useState({
    name: '',
    email: '',
    phone: '',
    role: 'agent',
    permission_level: 'read_write',
    job_title: '',
    assigned_buildings: [],
  });
  
  const [assignmentData, setAssignmentData] = useState({
    buildingId: '',
    permissionLevel: 'read_write',
  });
  
  const [tempPassword, setTempPassword] = useState('');
  const [showTempPassword, setShowTempPassword] = useState(false);

  useEffect(() => {
    fetchCompanyData();
  }, []);

  const fetchCompanyData = async () => {
    setLoading(true);
    try {
      // Get company dashboard data first
      const dashRes = await fetch(`${API_URL}/api/agent-company/my-dashboard`, {
        credentials: 'include'
      });
      
      if (dashRes.ok) {
        const data = await dashRes.json();
        setCompanyId(data.company?.company_id);
        
        if (data.company?.company_id) {
          // Fetch staff and buildings in parallel
          const [staffRes, buildingsRes] = await Promise.all([
            fetch(`${API_URL}/api/agent-company/staff/${data.company.company_id}`, { credentials: 'include' }),
            fetch(`${API_URL}/api/agent-company/buildings/${data.company.company_id}`, { credentials: 'include' })
          ]);
          
          if (staffRes.ok) {
            setStaff(await staffRes.json());
          }
          if (buildingsRes.ok) {
            setBuildings(await buildingsRes.json());
          }
        }
      } else {
        // Fallback to demo data
        setStaff([
          { agent_id: '1', name: 'John Smith', email: 'john@propmanage.co.za', phone: '+27 82 123 4567', role: 'super_agent', status: 'active', permission_level: 'read_write', assigned_buildings: ['demo_1'] },
          { agent_id: '2', name: 'Sarah Johnson', email: 'sarah@propmanage.co.za', phone: '+27 83 234 5678', role: 'agent', status: 'active', permission_level: 'read_write', assigned_buildings: ['demo_1', 'demo_2'] },
          { agent_id: '3', name: 'Mike Williams', email: 'mike@propmanage.co.za', phone: '+27 84 345 6789', role: 'agent', status: 'inactive', permission_level: 'read_only', assigned_buildings: [] },
        ]);
        setBuildings([
          { hoa_id: 'demo_1', name: 'Palm Gardens Complex', unit_count: 24, address: '123 Palm Street, Cape Town' },
          { hoa_id: 'demo_2', name: 'Ocean View Apartments', unit_count: 48, address: '456 Beach Road, Durban' },
        ]);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load staff data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddStaff = async () => {
    if (!newStaff.name || !newStaff.email) {
      toast.error('Please fill in required fields');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/agent-company/staff/${companyId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(newStaff)
      });

      if (response.ok) {
        const data = await response.json();
        setTempPassword(data.temp_password);
        setShowAddDialog(false);
        setShowPasswordDialog(true);
        setNewStaff({ name: '', email: '', phone: '', role: 'agent', permission_level: 'read_write', job_title: '', assigned_buildings: [] });
        fetchCompanyData();
        toast.success('Staff member created successfully');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create staff member');
      }
    } catch (error) {
      toast.error('Failed to create staff member');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateStaff = async () => {
    if (!selectedStaff) return;

    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/agent-company/staff/${companyId}/${selectedStaff.agent_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: selectedStaff.name,
          phone: selectedStaff.phone,
          permission_level: selectedStaff.permission_level,
          job_title: selectedStaff.job_title,
          status: selectedStaff.status,
        })
      });

      if (response.ok) {
        toast.success('Staff member updated');
        setShowEditDialog(false);
        setSelectedStaff(null);
        fetchCompanyData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update');
      }
    } catch (error) {
      toast.error('Failed to update staff member');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemoveStaff = async (agentId) => {
    if (!window.confirm('Are you sure you want to remove this staff member?')) return;

    try {
      const response = await fetch(`${API_URL}/api/agent-company/staff/${companyId}/${agentId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Staff member removed');
        fetchCompanyData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to remove');
      }
    } catch (error) {
      toast.error('Failed to remove staff member');
    }
  };

  const handleResetPassword = async (agentId) => {
    try {
      const response = await fetch(`${API_URL}/api/agent-company/staff/${companyId}/${agentId}/reset-password`, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setTempPassword(data.temp_password);
        setShowPasswordDialog(true);
        toast.success('Password reset successfully');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to reset password');
      }
    } catch (error) {
      toast.error('Failed to reset password');
    }
  };

  const handleAssignBuilding = async () => {
    if (!selectedStaff || !assignmentData.buildingId) {
      toast.error('Please select a building');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/agent-company/buildings/${companyId}/assign-agent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          agent_id: selectedStaff.agent_id,
          hoa_id: assignmentData.buildingId,
          permission_level: assignmentData.permissionLevel
        })
      });

      if (response.ok) {
        toast.success('Building assigned successfully');
        setShowAssignDialog(false);
        setSelectedStaff(null);
        setAssignmentData({ buildingId: '', permissionLevel: 'read_write' });
        fetchCompanyData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to assign building');
      }
    } catch (error) {
      toast.error('Failed to assign building');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRevokeBuilding = async (agentId, hoaId) => {
    if (!window.confirm('Remove access to this building?')) return;

    try {
      const response = await fetch(`${API_URL}/api/agent-company/buildings/${companyId}/revoke-agent/${agentId}/${hoaId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Building access revoked');
        fetchCompanyData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to revoke access');
      }
    } catch (error) {
      toast.error('Failed to revoke access');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const filteredStaff = staff.filter(s =>
    s.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    s.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getRoleBadge = (role) => {
    if (role === 'super_agent') return <Badge className="bg-purple-100 text-purple-700">Super Agent</Badge>;
    return <Badge className="bg-blue-100 text-blue-700">Agent</Badge>;
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-emerald-100 text-emerald-700"><CheckCircle2 className="w-3 h-3 mr-1" />Active</Badge>;
      case 'suspended':
        return <Badge className="bg-amber-100 text-amber-700"><AlertTriangle className="w-3 h-3 mr-1" />Suspended</Badge>;
      case 'inactive':
        return <Badge className="bg-red-100 text-red-700"><XCircle className="w-3 h-3 mr-1" />Inactive</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const getPermissionBadge = (level) => {
    if (level === 'read_write') return <Badge variant="outline" className="border-emerald-300 text-emerald-700">Read & Write</Badge>;
    return <Badge variant="outline" className="border-slate-300 text-slate-500">Read Only</Badge>;
  };

  const getBuildingName = (hoaId) => {
    const building = buildings.find(b => b.hoa_id === hoaId);
    return building?.name || hoaId;
  };

  const getAvailableBuildings = (staffMember) => {
    return buildings.filter(b => !staffMember?.assigned_buildings?.includes(b.hoa_id));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 p-4 lg:p-8" data-testid="agent-staff-page">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <Button variant="ghost" className="mb-2 -ml-2" onClick={() => navigate('/agent')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold text-slate-900 flex items-center gap-3">
                <Users className="w-8 h-8 text-indigo-600" />
                Staff Management
              </h1>
              <p className="text-slate-500 mt-1">Manage your team members and building assignments</p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={fetchCompanyData}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
              <Button onClick={() => setShowAddDialog(true)} data-testid="add-staff-btn" className="bg-indigo-600 hover:bg-indigo-700">
                <Plus className="w-4 h-4 mr-2" />
                Add Staff
              </Button>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-white border border-slate-200 p-1 mb-6">
            <TabsTrigger value="staff" className="data-[state=active]:bg-indigo-50 data-[state=active]:text-indigo-700">
              <Users className="w-4 h-4 mr-2" />
              Staff Members
            </TabsTrigger>
            <TabsTrigger value="assignments" className="data-[state=active]:bg-indigo-50 data-[state=active]:text-indigo-700">
              <Building2 className="w-4 h-4 mr-2" />
              Building Assignments
            </TabsTrigger>
          </TabsList>

          {/* Staff Tab */}
          <TabsContent value="staff">
            {/* Search */}
            <div className="mb-4">
              <div className="relative max-w-md">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  placeholder="Search by name or email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>

            {/* Staff List */}
            {filteredStaff.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <Users className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No Staff Members</h3>
                  <p className="text-slate-500 mb-4">Add your first team member to get started.</p>
                  <Button onClick={() => setShowAddDialog(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add Staff Member
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {filteredStaff.map(member => (
                  <Card key={member.agent_id} data-testid={`staff-${member.agent_id}`}>
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between gap-4 flex-wrap">
                        <div className="flex items-center gap-4 min-w-0 flex-1">
                          <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-semibold text-lg flex-shrink-0 ${
                            member.role === 'super_agent' ? 'bg-purple-500' : 'bg-indigo-500'
                          }`}>
                            {member.name?.charAt(0)?.toUpperCase() || 'U'}
                          </div>
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <h3 className="font-semibold text-lg truncate">{member.name}</h3>
                              {getRoleBadge(member.role)}
                              {getStatusBadge(member.status)}
                            </div>
                            <div className="flex items-center gap-4 text-sm text-slate-500 mt-1 flex-wrap">
                              <span className="flex items-center gap-1">
                                <Mail className="w-4 h-4" />
                                {member.email}
                              </span>
                              {member.phone && (
                                <span className="flex items-center gap-1">
                                  <Phone className="w-4 h-4" />
                                  {member.phone}
                                </span>
                              )}
                              {member.job_title && (
                                <span className="flex items-center gap-1">
                                  <Briefcase className="w-4 h-4" />
                                  {member.job_title}
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-2 mt-2 flex-wrap">
                              <span className="text-xs text-slate-500">Buildings:</span>
                              {member.role === 'super_agent' ? (
                                <Badge variant="outline" className="text-xs">All Buildings</Badge>
                              ) : member.assigned_buildings?.length > 0 ? (
                                member.assigned_buildings.map(hoaId => (
                                  <Badge key={hoaId} variant="secondary" className="text-xs">
                                    {getBuildingName(hoaId)}
                                  </Badge>
                                ))
                              ) : (
                                <span className="text-xs text-slate-400 italic">No buildings assigned</span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {member.role !== 'super_agent' && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setSelectedStaff(member);
                                setShowAssignDialog(true);
                              }}
                              title="Assign Building"
                            >
                              <Building2 className="w-4 h-4" />
                            </Button>
                          )}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setSelectedStaff({ ...member });
                              setShowEditDialog(true);
                            }}
                            title="Edit"
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleResetPassword(member.agent_id)}
                            title="Reset Password"
                          >
                            <Key className="w-4 h-4" />
                          </Button>
                          {member.role !== 'super_agent' && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-red-500 hover:text-red-700 hover:border-red-300"
                              onClick={() => handleRemoveStaff(member.agent_id)}
                              title="Remove"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Building Assignments Tab */}
          <TabsContent value="assignments">
            <div className="space-y-6">
              {staff.filter(s => s.role !== 'super_agent').map(member => (
                <Card key={member.agent_id}>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-indigo-500 flex items-center justify-center text-white font-semibold">
                          {member.name?.charAt(0)?.toUpperCase() || 'U'}
                        </div>
                        <div>
                          <CardTitle className="text-lg">{member.name}</CardTitle>
                          <CardDescription>{member.email}</CardDescription>
                        </div>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setSelectedStaff(member);
                          setShowAssignDialog(true);
                        }}
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Assign Building
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {member.assigned_buildings?.length > 0 ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {member.assigned_buildings.map(hoaId => {
                          const building = buildings.find(b => b.hoa_id === hoaId);
                          return (
                            <div key={hoaId} className="p-3 bg-slate-50 rounded-lg flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <Building2 className="w-4 h-4 text-slate-400" />
                                <div>
                                  <p className="font-medium text-sm">{building?.name || hoaId}</p>
                                  <p className="text-xs text-slate-500">{building?.unit_count || 0} units</p>
                                </div>
                              </div>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-red-500 hover:text-red-700 h-8 w-8 p-0"
                                onClick={() => handleRevokeBuilding(member.agent_id, hoaId)}
                                title="Remove Access"
                              >
                                <XCircle className="w-4 h-4" />
                              </Button>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="text-center py-6 text-slate-400">
                        <Building2 className="w-8 h-8 mx-auto mb-2" />
                        <p className="text-sm">No buildings assigned</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}

              {/* Super Agents section */}
              {staff.filter(s => s.role === 'super_agent').length > 0 && (
                <Card className="border-purple-200 bg-purple-50/50">
                  <CardHeader>
                    <CardTitle className="text-purple-800 flex items-center gap-2">
                      <Shield className="w-5 h-5" />
                      Super Agents (Full Access)
                    </CardTitle>
                    <CardDescription className="text-purple-600">
                      Super Agents have access to all buildings managed by the company
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-3">
                      {staff.filter(s => s.role === 'super_agent').map(sa => (
                        <div key={sa.agent_id} className="flex items-center gap-2 px-3 py-2 bg-white rounded-lg border border-purple-200">
                          <div className="w-8 h-8 rounded-full bg-purple-500 flex items-center justify-center text-white text-sm font-semibold">
                            {sa.name?.charAt(0)?.toUpperCase() || 'U'}
                          </div>
                          <span className="font-medium text-sm">{sa.name}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Add Staff Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-indigo-600" />
              Add Staff Member
            </DialogTitle>
            <DialogDescription>
              Create a new agent account. They will receive a temporary password to sign in.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Full Name *</Label>
                <Input
                  value={newStaff.name}
                  onChange={(e) => setNewStaff({ ...newStaff, name: e.target.value })}
                  placeholder="John Smith"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={newStaff.email}
                  onChange={(e) => setNewStaff({ ...newStaff, email: e.target.value })}
                  placeholder="john@company.com"
                  className="mt-1"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Phone</Label>
                <Input
                  value={newStaff.phone}
                  onChange={(e) => setNewStaff({ ...newStaff, phone: e.target.value })}
                  placeholder="+27 82 123 4567"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Job Title</Label>
                <Input
                  value={newStaff.job_title}
                  onChange={(e) => setNewStaff({ ...newStaff, job_title: e.target.value })}
                  placeholder="Property Manager"
                  className="mt-1"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Role</Label>
                <Select
                  value={newStaff.role}
                  onValueChange={(v) => setNewStaff({ ...newStaff, role: v })}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="agent">General Agent</SelectItem>
                    <SelectItem value="super_agent">Super Agent (Admin)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Permission Level</Label>
                <Select
                  value={newStaff.permission_level}
                  onValueChange={(v) => setNewStaff({ ...newStaff, permission_level: v })}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="read_write">Read & Write</SelectItem>
                    <SelectItem value="read_only">Read Only</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {newStaff.role === 'agent' && buildings.length > 0 && (
              <div>
                <Label>Assign Buildings</Label>
                <div className="mt-2 max-h-32 overflow-y-auto border border-slate-200 rounded-md p-2">
                  {buildings.map(building => (
                    <label key={building.hoa_id} className="flex items-center gap-2 p-2 hover:bg-slate-50 rounded cursor-pointer">
                      <input
                        type="checkbox"
                        checked={newStaff.assigned_buildings.includes(building.hoa_id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setNewStaff({ ...newStaff, assigned_buildings: [...newStaff.assigned_buildings, building.hoa_id] });
                          } else {
                            setNewStaff({ ...newStaff, assigned_buildings: newStaff.assigned_buildings.filter(id => id !== building.hoa_id) });
                          }
                        }}
                        className="rounded border-slate-300"
                      />
                      <span className="text-sm">{building.name}</span>
                      <span className="text-xs text-slate-400">({building.unit_count} units)</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
            <Button onClick={handleAddStaff} disabled={submitting} className="bg-indigo-600 hover:bg-indigo-700">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create Staff'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Staff Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit2 className="w-5 h-5 text-blue-600" />
              Edit Staff Member
            </DialogTitle>
          </DialogHeader>
          {selectedStaff && (
            <div className="space-y-4 py-4">
              <div>
                <Label>Full Name</Label>
                <Input
                  value={selectedStaff.name}
                  onChange={(e) => setSelectedStaff({ ...selectedStaff, name: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Phone</Label>
                <Input
                  value={selectedStaff.phone || ''}
                  onChange={(e) => setSelectedStaff({ ...selectedStaff, phone: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Job Title</Label>
                <Input
                  value={selectedStaff.job_title || ''}
                  onChange={(e) => setSelectedStaff({ ...selectedStaff, job_title: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Permission Level</Label>
                  <Select
                    value={selectedStaff.permission_level}
                    onValueChange={(v) => setSelectedStaff({ ...selectedStaff, permission_level: v })}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="read_write">Read & Write</SelectItem>
                      <SelectItem value="read_only">Read Only</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Status</Label>
                  <Select
                    value={selectedStaff.status}
                    onValueChange={(v) => setSelectedStaff({ ...selectedStaff, status: v })}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="suspended">Suspended</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowEditDialog(false); setSelectedStaff(null); }}>Cancel</Button>
            <Button onClick={handleUpdateStaff} disabled={submitting} className="bg-blue-600 hover:bg-blue-700">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Building Dialog */}
      <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-emerald-600" />
              Assign Building
            </DialogTitle>
            <DialogDescription>
              Assign a building to {selectedStaff?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Select Building</Label>
              <Select
                value={assignmentData.buildingId}
                onValueChange={(v) => setAssignmentData({ ...assignmentData, buildingId: v })}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Choose a building..." />
                </SelectTrigger>
                <SelectContent>
                  {getAvailableBuildings(selectedStaff).map(building => (
                    <SelectItem key={building.hoa_id} value={building.hoa_id}>
                      {building.name} ({building.unit_count} units)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {getAvailableBuildings(selectedStaff).length === 0 && (
                <p className="text-sm text-amber-600 mt-2">All buildings are already assigned to this agent.</p>
              )}
            </div>
            <div>
              <Label>Permission Level</Label>
              <Select
                value={assignmentData.permissionLevel}
                onValueChange={(v) => setAssignmentData({ ...assignmentData, permissionLevel: v })}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="read_write">Read & Write (Full Access)</SelectItem>
                  <SelectItem value="read_only">Read Only (View Only)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowAssignDialog(false); setSelectedStaff(null); }}>Cancel</Button>
            <Button 
              onClick={handleAssignBuilding} 
              disabled={submitting || !assignmentData.buildingId}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Assign Building'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Temporary Password Dialog */}
      <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="w-5 h-5 text-amber-600" />
              Temporary Password
            </DialogTitle>
            <DialogDescription>
              Share this password securely with the staff member. They will be prompted to change it on first login.
            </DialogDescription>
          </DialogHeader>
          <div className="py-6">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-center">
              <p className="text-sm text-amber-700 mb-2">Temporary Password:</p>
              <div className="flex items-center justify-center gap-2">
                <code className="text-lg font-mono font-bold text-amber-800 bg-white px-4 py-2 rounded border">
                  {showTempPassword ? tempPassword : '••••••••••••'}
                </code>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowTempPassword(!showTempPassword)}
                  className="h-8 w-8 p-0"
                >
                  {showTempPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => copyToClipboard(tempPassword)}
                  className="h-8 w-8 p-0"
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </div>
            <p className="text-xs text-slate-500 mt-4 text-center">
              The staff member can log in at the login page using the "Agent Login" tab with their email and this password.
            </p>
          </div>
          <DialogFooter>
            <Button onClick={() => { setShowPasswordDialog(false); setTempPassword(''); setShowTempPassword(false); }}>
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
